// apps/office/src/components/office/InteriorGroupsManager.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Plus, Trash2, Edit2, X, Check, Layers } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const InteriorGroupsManager = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  const { data: items = [], isLoading, refetch } = useQuery({
    queryKey: ['interior-groups-manager'],
    queryFn: () =>
      pb.collection('interior_product_groups').getFullList({ sort: 'sortOrder,name' }),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: async (name) => {
      const maxOrder = items.reduce((m, i) => Math.max(m, i.sortOrder || 0), 0);
      return pb.collection('interior_product_groups').create({
        name: name.trim(),
        sortOrder: maxOrder + 1,
        isActive: true,
      });
    },
    onSuccess: () => {
      toast.success('✅ Group added');
      setNewName('');
      queryClient.invalidateQueries(['interior-groups-manager']);
      queryClient.invalidateQueries(['interior-groups']);
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }) =>
      pb.collection('interior_product_groups').update(id, { name: name.trim() }),
    onSuccess: () => {
      toast.success('✅ Updated');
      setEditingId(null);
      setEditName('');
      queryClient.invalidateQueries(['interior-groups-manager']);
      queryClient.invalidateQueries(['interior-groups']);
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }) =>
      pb.collection('interior_product_groups').update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['interior-groups-manager']);
      queryClient.invalidateQueries(['interior-groups']);
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('interior_product_groups').delete(id),
    onSuccess: () => {
      toast.success('Deleted');
      queryClient.invalidateQueries(['interior-groups-manager']);
      queryClient.invalidateQueries(['interior-groups']);
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
  });

  const handleAdd = () => {
    if (!newName.trim()) return toast.error('Enter a name');
    if (items.some((i) => i.name.toLowerCase() === newName.trim().toLowerCase())) {
      return toast.error('Already exists');
    }
    createMutation.mutate(newName);
  };

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            Manage Groups (Rooms)
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Group products by room — Bed Room, Kitchen, Living Room, etc.
          </p>
        </DialogHeader>

        <div className="flex gap-2 py-2">
          <Input
            placeholder="New group name (e.g. Bed Room)…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Button
            onClick={handleAdd}
            disabled={createMutation.isPending || !newName.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
          >
            {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </Button>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : items.length === 0 ? (
            <p className="text-center py-8 text-sm text-slate-400">No groups yet</p>
          ) : (
            items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50/40">
                {editingId === item.id ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') updateMutation.mutate({ id: item.id, name: editName });
                        if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
                      }}
                      autoFocus
                      className="flex-1 h-8"
                    />
                    <Button size="sm" onClick={() => updateMutation.mutate({ id: item.id, name: editName })} className="h-8 bg-emerald-600 text-white px-2">
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditName(''); }} className="h-8 px-2">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className={`flex-1 text-sm font-medium ${item.isActive ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                      {item.name}
                    </span>
                    <button
                      onClick={() => toggleActiveMutation.mutate({ id: item.id, isActive: !item.isActive })}
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                        item.isActive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}
                    >
                      {item.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(item.id); setEditName(item.name); }} className="h-8 px-2 text-slate-500 hover:text-slate-800">
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm(`Delete "${item.name}"?`)) deleteMutation.mutate(item.id);
                      }}
                      className="h-8 px-2 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InteriorGroupsManager;
